import { lazy, Suspense, useEffect, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { LogOut, Menu, Search, Settings as SettingsIcon } from 'lucide-react';
import { useUIStore } from '@/lib/store';
import { useUser, useOrganization } from '@/features/auth/hooks';
import { useLogoutMutation } from '@/features/auth/api';
import { LanguageSwitcher } from './LanguageSwitcher';
import { ScaleSwitcher } from './ScaleSwitcher';

// Lazy: paletti vetää mukanaan käyttöoppaan sisällön (~13 kB gzip) -- ladataan
// vasta ensimmäisellä avauksella, ei päänippuun.
const CommandPalette = lazy(() =>
  import('@/features/assistant/CommandPalette').then((m) => ({ default: m.CommandPalette })),
);

/*
 * Header (Sessio 6a paivitys: user-menu + logout).
 *
 * Sisalto:
 * - Hampurilaispainike (mobile-drawer, C4-paatos)
 * - Leivanmuru-polku (URL -> i18n-avain)
 * - AI-avustaja-painike (cmd+K): avaa komentopaletin (assistance.md kerros 3a);
 *   AI-vastaukset tulevat samaan palettiin kerroksessa 3b
 * - User-menu: avatar + dropdown (kayttajatieto / Asetukset / Kirjaudu ulos)
 *
 * User-menu on toteutettu ilman Radix:ia (T9-saasto). useState avaa/sulkee,
 * click-outside + Esc sulkevat, focus-trapping kevyt (vain Tab pyorii itsessaan
 * react-natiivisesti -- ei pakoteta).
 */

const routeKeys: Record<string, string> = {
  '/': 'sidebar.home',
  '/ideoi': 'sidebar.ideoi',
  '/suunnittele': 'sidebar.suunnittele',
  '/tuota': 'sidebar.tuota',
  '/julkaise': 'sidebar.julkaise',
  '/seuraa': 'sidebar.seuraa',
  '/library': 'sidebar.library',
  '/settings': 'sidebar.settings',
};

function getInitial(name: string | undefined, email: string | undefined): string {
  const source = name ?? email ?? '';
  const trimmed = source.trim();
  return trimmed.length > 0 ? trimmed[0]!.toUpperCase() : '?';
}

export function Header() {
  const location = useLocation();
  const { t } = useTranslation();
  const toggleSidebarMobile = useUIStore((s) => s.toggleSidebarMobile);
  const user = useUser();
  const organization = useOrganization();
  const logoutMutation = useLogoutMutation();
  const [menuOpen, setMenuOpen] = useState(false);
  const [paletteOpen, setPaletteOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  // Globaali ⌘K / Ctrl+K avaa ja sulkee paletin (paletti itse on lazy)
  useEffect(() => {
    function onKey(event: KeyboardEvent) {
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'k') {
        event.preventDefault();
        setPaletteOpen((v) => !v);
      }
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  const labelKey = routeKeys[location.pathname];
  const current = labelKey ? t(labelKey) : 'Luviamo';

  // Click-outside + Esc -kasittely
  useEffect(() => {
    if (!menuOpen) return;
    function onClick(event: MouseEvent) {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setMenuOpen(false);
      }
    }
    function onKey(event: KeyboardEvent) {
      if (event.key === 'Escape') setMenuOpen(false);
    }
    document.addEventListener('mousedown', onClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [menuOpen]);

  const handleLogout = () => {
    setMenuOpen(false);
    logoutMutation.mutate(undefined);
  };

  return (
    <header className="flex h-[var(--header-height)] items-center gap-3 border-b border-kaste bg-white px-3 lg:gap-3.5 lg:px-5">
      <button
        type="button"
        onClick={toggleSidebarMobile}
        aria-label={t('header.openMenu')}
        className="flex h-8 w-8 items-center justify-center rounded-sm text-metsa hover:bg-usva focus-ring lg:hidden"
      >
        <Menu className="h-5 w-5" aria-hidden="true" />
      </button>

      <div className="flex-1 text-base text-usva-t">
        <span className="font-medium text-metsa">{current}</span>
      </div>

      <button
        type="button"
        onClick={() => setPaletteOpen(true)}
        data-testid="header-assistant"
        className="hidden cursor-pointer items-center gap-2 rounded-sm border border-kaste bg-usva px-3 py-1.5 text-sm text-lehti hover:bg-valo focus-ring lg:flex lg:min-w-[240px]"
        aria-label={t('header.assistantAriaLabel')}
      >
        <Search className="h-3.5 w-3.5" aria-hidden="true" />
        <span>{t('header.askAssistant')}</span>
        <kbd className="ml-auto rounded border border-kaste bg-white px-1.5 py-0.5 text-xs font-normal text-usva-t">
          ⌘K
        </kbd>
      </button>

      <button
        type="button"
        onClick={() => setPaletteOpen(true)}
        data-testid="header-assistant-mobile"
        aria-label={t('header.assistantAriaLabel')}
        className="flex h-8 w-8 items-center justify-center rounded-sm text-metsa hover:bg-usva focus-ring lg:hidden"
      >
        <Search className="h-4 w-4" aria-hidden="true" />
      </button>

      {paletteOpen && (
        <Suspense fallback={null}>
          <CommandPalette open={paletteOpen} onOpenChange={setPaletteOpen} />
        </Suspense>
      )}

      <ScaleSwitcher />

      <LanguageSwitcher />

      <div ref={menuRef} className="relative">
        <button
          type="button"
          onClick={() => setMenuOpen((v) => !v)}
          aria-label={t('header.userAriaLabel')}
          aria-haspopup="menu"
          aria-expanded={menuOpen}
          title={user?.name ?? user?.email ?? undefined}
          className="flex h-7 w-7 items-center justify-center rounded-full bg-sammal text-xs font-medium text-white focus-ring"
        >
          {getInitial(user?.name, user?.email)}
        </button>

        {menuOpen && (
          <div
            role="menu"
            aria-label={t('header.menu.menuAriaLabel')}
            className="absolute right-0 top-9 z-30 w-64 overflow-hidden rounded-sm border border-kaste bg-white shadow-lg"
          >
            <div className="border-b border-kaste px-3 py-2">
              <p className="truncate text-sm font-medium text-teksti">
                {user?.name ?? t('header.menu.unknownUser')}
              </p>
              <p className="truncate text-xs text-usva-t">{user?.email ?? ''}</p>
              {organization && (
                <p className="mt-1 truncate text-xs text-usva-t">{organization.name}</p>
              )}
            </div>
            <div className="flex flex-col py-1">
              <Link
                to="/settings"
                role="menuitem"
                onClick={() => setMenuOpen(false)}
                className="flex items-center gap-2 px-3 py-2 text-sm text-teksti hover:bg-usva focus-ring"
              >
                <SettingsIcon className="h-4 w-4" aria-hidden="true" />
                <span>{t('header.menu.settings')}</span>
              </Link>
              <button
                type="button"
                role="menuitem"
                onClick={handleLogout}
                disabled={logoutMutation.isPending}
                className="flex items-center gap-2 px-3 py-2 text-left text-sm text-teksti hover:bg-usva focus-ring disabled:cursor-not-allowed disabled:opacity-60"
              >
                <LogOut className="h-4 w-4" aria-hidden="true" />
                <span>
                  {logoutMutation.isPending ? t('common.loading') : t('header.menu.signOut')}
                </span>
              </button>
            </div>
            <div className="border-t border-kaste px-3 py-1.5">
              <p className="text-[11px] text-usva-t" data-testid="app-version">
                {t('header.menu.version', { version: __APP_VERSION__ })}
              </p>
            </div>
          </div>
        )}
      </div>
    </header>
  );
}
